import type { Product } from 'src/components/restructure/product/types/product'
import { formatterPrice } from 'src/utils/formatterPrice'
import { Image } from 'src/components/ui/Image'
import { useState } from 'react'

import { KitlookSkuSelector } from './KitlookSkuSelector'
import type { SelectedProduct } from './KitlookItemsConfirmation'

interface KitlookItemSelectionProps {
  product: Product
  toggleProduct: (productId: string) => void
  selectSku: (productId: string, skuId: string) => void
  selectedProducts: SelectedProduct[]
}

export function KitlookItemSelection({
  product,
  toggleProduct,
  selectSku,
  selectedProducts,
}: KitlookItemSelectionProps) {
  const selection = selectedProducts.find(
    (selectedProduct) => selectedProduct.productId === product.productId
  )

  const isSelected = selection?.isSelected
  const firstAvailable = product.items.find(
    (item) => item.isAvailable && item.productId === product.productId
  )

  const [isSkuSelectOpen, setIsSkuSelectOpen] = useState(false)

  if (!firstAvailable) return null

  const hasDiscount =
    firstAvailable.sellerDefault.sellerOffer.listPrice >
    firstAvailable.sellerDefault.sellerOffer.cashPrice

  const { cashDiscountPercentage, cashPrice, listPrice, price } =
    firstAvailable.sellerDefault.sellerOffer

  const [firstImage] = firstAvailable.images

  return (
    <div
      key={product.productId}
      className={`flex flex-col gap-4 pb-6 transition-all duration-300 ${
        isSkuSelectOpen ? 'z-[9999]' : 'z-100'
      } ${!isSelected ? 'opacity-50 scale-[0.96] origin-left' : ''}`}
    >
      <div className="flex gap-4 items-start">
        <Image
          src={firstImage.imageUrl}
          alt={product.productName}
          className="w-20 h-20 object-contain bg-gray-50 rounded"
        />

        <div className="flex-1 flex flex-col gap-1">
          <div className="flex justify-between gap-2">
            <h3 className="text-sm font-medium text-gray-800 line-clamp-3">
              {product.productName}
            </h3>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => {
                toggleProduct(product.productId)
              }}
              className="w-[18px] h-[18px] flex-shrink-0 rounded border-[#B3BAC3] text-primaryBlue focus:ring-backgroundPrimary"
            />
          </div>

          <div className="flex flex-col">
            {hasDiscount && (
              <span className="text-xs text-gray-400 line-through">
                {formatterPrice(listPrice)}
              </span>
            )}

            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-900">
                {formatterPrice(cashPrice)} {cashPrice !== price && 'à vista'}
              </span>
              {cashDiscountPercentage > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold px-1 py-0.5 rounded">
                  {`${cashDiscountPercentage}%  OFF`}
                </span>
              )}
            </div>
            {price > cashPrice && (
              <span className="font-medium text-secondary text-[10px]">
                ou{' '}
                <strong className="font-semibold">
                  {formatterPrice(price)}
                </strong>{' '}
                no cartão
              </span>
            )}
          </div>
        </div>
      </div>
      {isSelected && (
        <KitlookSkuSelector
          product={product as unknown as Product}
          filters={['Cor']}
          isSkuSelectOpen={isSkuSelectOpen}
          setIsSkuSelectOpen={setIsSkuSelectOpen}
          onSelect={(sku) => selectSku(product.productId, sku)}
          selectedSku={selection.skuId ?? null}
        />
      )}
    </div>
  )
}
